/* Cast London — Theme JS v1.0 */
'use strict';

/* ---- Mobile menu ----------------------------------------- */
class MobileMenu {
  constructor() {
    this.btn     = document.querySelector('[data-menu-toggle]');
    this.nav     = document.querySelector('[data-mobile-nav]');
    this.overlay = document.querySelector('[data-menu-overlay]');
    this.body    = document.body;

    if (!this.btn || !this.nav) return;

    this.btn.addEventListener('click', () => this.toggle());
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  toggle() {
    const isOpen = this.nav.getAttribute('aria-hidden') === 'false';
    isOpen ? this.close() : this.open();
  }

  open() {
    this.nav.setAttribute('aria-hidden', 'false');
    this.btn.setAttribute('aria-expanded', 'true');
    this.body.classList.add('menu-open');
    this.body.style.overflow = 'hidden';
  }

  close() {
    this.nav.setAttribute('aria-hidden', 'true');
    this.btn.setAttribute('aria-expanded', 'false');
    this.body.classList.remove('menu-open');
    this.body.style.overflow = '';
  }
}

/* ---- Nav state: dark/light based on section beneath ------- */
class HeaderScroll {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;

    // Sections with light backgrounds that trigger nav--light
    this.lightSections = document.querySelectorAll('.s--parch, .s--off');
    this.threshold = 40;

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.onScroll();
  }

  onScroll() {
    const scrollY = window.scrollY;

    // Add scrolled class once past threshold (solidifies background)
    this.header.classList.toggle('nav--scrolled', scrollY > this.threshold);

    // Check if nav bar is positioned over a light section
    let overLight = false;
    if (this.lightSections.length > 0) {
      this.lightSections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        // Section overlaps nav zone if its top is above nav bottom and bottom is below nav top
        if (rect.top < 68 && rect.bottom > 0) {
          overLight = true;
        }
      });
    }

    if (overLight) {
      this.header.classList.remove('nav--dark');
      this.header.classList.add('nav--light');
    } else {
      this.header.classList.remove('nav--light');
      this.header.classList.add('nav--dark');
    }
  }
}

/* ---- Desktop mega-menu (hover + focus, delayed) ---------- */
/* Toggles .is-open on the nav item only — never touches [data-header],
   so HeaderScroll's nav-light logic is unaffected. */
class MegaMenu {
  constructor() {
    this.items = document.querySelectorAll('[data-nav-item]');
    if (!this.items.length) return;

    this.openItem  = null;
    this.openTimer = null;
    this.closeTimer = null;

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-nav-trigger]');
      const panel   = item.querySelector('[data-nav-panel]');
      if (!trigger || !panel) return;

      item.addEventListener('mouseenter', () => this.scheduleOpen(item));
      item.addEventListener('mouseleave', () => this.scheduleClose(item));

      // Focus: open when focus enters, close when it leaves the item entirely
      item.addEventListener('focusin', () => this.open(item));
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) this.close(item);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.open(item);
          const first = panel.querySelector('a');
          if (first) first.focus();
        } else if (e.key === 'Escape') {
          this.close(item);
          trigger.focus();
        }
      });

      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close(item);
          trigger.focus();
        }
      });
    });
  }

  scheduleOpen(item) {
    clearTimeout(this.closeTimer);
    this.openTimer = setTimeout(() => this.open(item), 120);
  }

  scheduleClose(item) {
    clearTimeout(this.openTimer);
    this.closeTimer = setTimeout(() => this.close(item), 200);
  }

  open(item) {
    clearTimeout(this.closeTimer);
    if (this.openItem && this.openItem !== item) this.close(this.openItem);
    item.classList.add('is-open');
    const t = item.querySelector('[data-nav-trigger]');
    if (t) t.setAttribute('aria-expanded', 'true');
    this.openItem = item;
  }

  close(item) {
    item.classList.remove('is-open');
    const t = item.querySelector('[data-nav-trigger]');
    if (t) t.setAttribute('aria-expanded', 'false');
    if (this.openItem === item) this.openItem = null;
  }
}

/* ---- Mobile drawer accordion (parents with children) ---- */
function initMobileAccordion() {
  const toggles = document.querySelectorAll('[data-mobile-toggle]');
  if (!toggles.length) return;

  toggles.forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-mobile-group]');
      const sub   = group && group.querySelector('[data-mobile-sub]');
      if (!sub) return;
      const open = group.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      sub.hidden = !open;
    });
  });
}

/* ---- Hero video — honour reduced-motion ------------------ */
function initHeroVideo() {
  const video = document.querySelector('.hero__video');
  if (!video) return;

  // Reduced-motion users see the static poster (handled in CSS).
  // Stop the video decoding/playing in the background for them.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.removeAttribute('autoplay');
    video.pause();
  }
}

/* ---- Floating basket: hide over the footer --------------- */
function initFloatingBasket() {
  const pill   = document.querySelector('.floating-basket');
  const footer = document.querySelector('.site-footer, footer');
  if (!pill || !footer || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      document.body.classList.toggle('footer-in-view', entry.isIntersecting);
    });
  }, { rootMargin: '0px 0px -40px 0px' });

  observer.observe(footer);
}

/* ---- Scroll reveal (.r → .r.in) -------------------------- */
function initReveal() {
  const els = document.querySelectorAll('.r');
  if (!els.length) return;

  // No IO support / reduced motion → reveal immediately, no animation.
  if (!('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach((el) => el.classList.add('in'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('in');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08 });

  els.forEach((el) => observer.observe(el));
}

/* ---- Copy to clipboard ([data-copy]) --------------------- */
function initCopy() {
  const btns = document.querySelectorAll('[data-copy]');
  if (!btns.length) return;

  btns.forEach((btn) => {
    btn.addEventListener('click', async () => {
      const value = btn.dataset.copy;
      if (!value) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          // Fallback for non-secure contexts
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        const done = btn.dataset.copiedLabel || 'Copied';
        const orig = btn.dataset.copyLabel || btn.textContent;
        btn.textContent = done;
        btn.classList.add('is-copied');
        clearTimeout(btn._copyTimer);
        btn._copyTimer = setTimeout(() => {
          btn.textContent = orig;
          btn.classList.remove('is-copied');
        }, 1800);
      } catch (e) { /* clipboard unavailable — no-op */ }
    });
  });
}

/* ---- Lazy image observer --------------------------------- */
function initLazyImages() {
  const images = document.querySelectorAll('img[data-src]');
  if (!images.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      if (img.dataset.src)    img.src    = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      observer.unobserve(img);
    });
  }, { rootMargin: '200px 0px' });

  images.forEach((img) => observer.observe(img));
}

/* ---- Quantity controls ----------------------------------- */
function initQuantityControls() {
  document.querySelectorAll('.quantity').forEach((wrap) => {
    const input = wrap.querySelector('.quantity__input');
    const dec   = wrap.querySelector('[data-qty-minus]');
    const inc   = wrap.querySelector('[data-qty-plus]');
    if (!input) return;

    dec?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > parseInt(input.min || 1, 10)) {
        input.value = val - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    inc?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      const max = parseInt(input.max || 9999, 10);
      if (val < max) {
        input.value = val + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });
}

/* ---- Init ------------------------------------------------ */
document.addEventListener('DOMContentLoaded', () => {
  initHeroVideo();        // before lazy observer — hero is above the fold
  new MobileMenu();
  new HeaderScroll();
  new MegaMenu();
  initMobileAccordion();
  initReveal();
  initCopy();
  initFloatingBasket();
  initLazyImages();
  initQuantityControls();
});
